// Randomizes the visible portfolio card per section on every page load.
// The server renders every available portfolio item per section (with the
// non-default ones marked `hidden`) so that Sitefinity's output cache stays
// effective. This script picks a fresh random card per section client-side
// and ensures the same portfolio doesn't appear in multiple sections at once.

const shuffle = <T>(input: T[]): T[] => {
    const out = [...input];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
};

const randomizeContainer = (container: HTMLElement) => {
    const cards = Array.from(
        container.querySelectorAll<HTMLElement>('.portfolio-cards__card[data-section-index]')
    );

    if (!cards.length) {
        return;
    }

    // Group cards by their section index, preserving the original section order.
    const sectionsMap = new Map<string, HTMLElement[]>();
    cards.forEach(card => {
        const idx = card.dataset.sectionIndex ?? '';
        const list = sectionsMap.get(idx) ?? [];
        list.push(card);
        sectionsMap.set(idx, list);
    });

    const sortedSectionKeys = Array.from(sectionsMap.keys()).sort(
        (a, b) => Number(a) - Number(b)
    );

    const usedPortfolioIds = new Set<string>();

    sortedSectionKeys.forEach(key => {
        const sectionCards = sectionsMap.get(key) ?? [];
        if (!sectionCards.length) {
            return;
        }

        const shuffled = shuffle(sectionCards);

        // Prefer a card whose portfolio hasn't been chosen for another section
        // yet. Fall back to the first available card so the section still renders.
        const pick =
            shuffled.find(card => !usedPortfolioIds.has(card.dataset.portfolioId ?? '')) ??
            shuffled[0];

        const pickId = pick.dataset.portfolioId ?? '';
        if (pickId) {
            usedPortfolioIds.add(pickId);
        }

        sectionCards.forEach(card => {
            if (card === pick) {
                card.removeAttribute('hidden');
            } else {
                card.setAttribute('hidden', 'hidden');
            }
        });
    });
};

export const portfolioCards = () => {
    document
        .querySelectorAll<HTMLElement>('[data-portfolio-cards]')
        .forEach(randomizeContainer);
};
